import { Box, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { GridColDef, GridPreProcessEditCellProps, GridSortCellParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';
import React, { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { getOptions, newGridRecordId } from '@shared/commonFunctions';
import { dataGridCol, gridBooleanCell, gridCurrencyCell, gridStateCell } from '@shared/components/grid/common';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';

import { ICountyOptions } from '../../../interfaces';
import { docFeesValidationSchema } from '../../../validation';
import { FormulaModal } from './FormulaModal';
import { calculationType, IDocFeesTableProps, IFormulaFields, options } from './interfaces';
import { requestCreateDocFee, requestDeleteDocFee, requestUpdateDocFee, requestUpdateTitleToDeal } from './queries';

const newRowModel = {
  category: 'Doc Fee',
  description: 'Doc Fee',
  isDocFee: true,
};

const preProcessAmount = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
  ...props,
  value: otherFieldsProps?.calculationType.value !== calculationType.Formula ? props.value : null,
});

const preProcessFormula = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
  ...props,
  value: otherFieldsProps?.calculationType.value !== calculationType.FlatFee ? props.value : null,
});

export const getCountyString = (countyList: ICountyOptions[]) =>
  (countyList || []).map((value: ICountyOptions) => value.countyName).join(', ') || 'All';

export const DocFeesTable = ({ data = [], lenderId, includeTitleFeeInDeal }: IDocFeesTableProps) => {
  const apiRef = useGridApiRef();

  const { data: countyData } = useSWR<ICountyOptions[]>(`${SERVICES.LENDER}/lenders/county`);

  const [modalData, setModalData] = useState<IFormulaFields | null>(null);
  const [loading, setLoading] = useState(false);

  const getCountyOptions = useCallback(
    (selectedState: string) => {
      const stateCountyOptions = countyData
        ?.filter((value) => value.countyState === selectedState)
        ?.map((value) => ({ ...value, label: `${value.countyState}-${value.countyName}` }));

      return stateCountyOptions || [];
    }, [countyData]);

  const handleChange = useCallback((value: unknown, row: IFormulaFields) => {
    if (value === calculationType.Formula) {
      const counties = getCountyOptions(row.state);
      const countyList = counties.filter((county) => !!row?.countyIds?.find((id) => id === county.id));

      setModalData({ ...row, countyList: countyList });
    }
  }, [getCountyOptions]);

  const columns: GridColDef[] = useMemo(() => [
    dataGridCol('state', gridStateCell),
    dataGridCol('countyIds', {
      headerName: 'County Name',
      flex: 1.8,
      textWrap: true,
      renderEditCell: (params) => {
        const options = getCountyOptions(params.row.state);

        return (
          <GridColSelect
            isSearchable
            multiple
            limitTags={1}
            placeholder={params?.value?.length ? '' : 'All'}
            options={getOptions(options, 'id', 'label')}
            params={params}
            placeholderOpacity={1}
          />
        );
      },
      valueFormatter: (params) => (params.id ? getCountyString(apiRef.current.getRow(params.id).countyList) : ''),
      sortComparator: (v1: string[], v2: string[], param1: GridSortCellParams, param2: GridSortCellParams) => {
        const value1 = getCountyString(apiRef.current.getRow(param1.id).countyList);
        const value2 = getCountyString(apiRef.current.getRow(param2.id).countyList);
        return value1.localeCompare(value2);
      },
      editable: true,
    }),
    dataGridCol('calculationType', {
      headerName: 'FlatFee/Formula',
      width: 150,
      renderEditCell: (params) => {
        const updatedParams = {
          ...params,
          value: typeof params.value == 'string' ?
            calculationType[params.value as keyof typeof calculationType]
            : params.value
        };

        return (
          <GridColSelect
            hideNoneOption
            isSearchable
            disabled={!params.row.state}
            options={options}
            params={updatedParams}
            onChange={(value) => handleChange(value, params.row)}
          />
        );
      },
      editable: true,
    }),
    dataGridCol('amount', {
      ...gridCurrencyCell,
      minWidth: 250,
      textWrap: true,
      preProcessEditCellProps: preProcessAmount,
      renderCell: (params) => {
        const isFormulaField = params.row.calculationType === calculationType[calculationType.Formula];
        if (isFormulaField) return <Link onClick={() => setModalData(params.row)}>{params.row.formula}</Link>;
        return params.value;
      },
      renderEditCell: (params) => {
        const type = typeof params.row.calculationType == 'string' ?
          calculationType[params.row.calculationType as keyof typeof calculationType]
          : params.row.calculationType;

        const isFormula = type === calculationType.Formula;
        const formulaParams = {
          ...params,
          value: params.row.formula
        };

        return (
          <>
            {!isFormula ? <GridColNumber
              disabled={!params.row.state}
              params={params}
              prefix="$"
              decimalScale={2}
            /> :
              <GridColTextInput disabled params={formulaParams} />
            }
          </>
        );
      },
      flex: 1,
      editable: true,
    }),
    dataGridCol('formula', {
      hide: true,
      editable: true,
      preProcessEditCellProps: preProcessFormula,
    }),
    dataGridCol('category', {
      renderEditCell: (params) => <GridColTextInput params={params} />,
      width: 130,
      editable: true,
    }),
    dataGridCol('description', {
      renderEditCell: (params) => <GridColTextInput params={params} />,
      width: 200,
      textWrap: true,
      editable: true,
    }),
    dataGridCol('isDocFee', { ...gridBooleanCell }),
  ].map(column => ({ ...column, minWidth: 80, })), [countyData, handleChange, preProcessAmount, preProcessFormula]);

  const gridActions = {
    onSave: async (row: GridValidRowModel) => {
      setLoading(true);
      const isStateMatched = getCountyOptions(row.state).find((value) => row.countyIds?.find((county: string) => county === value.id));
      const resp = row?.id === newGridRecordId
        ? await requestCreateDocFee(lenderId, { ...row, isDocFee: !!row.isDocFee, countyIds: isStateMatched ? row.countyIds : [] })
        : await requestUpdateDocFee(lenderId, { ...row, countyIds: isStateMatched ? row.countyIds : [] });
      setLoading(false);

      return resp;
    },
    onDelete: async (row: GridValidRowModel) => {
      setLoading(true);
      const resp = await requestDeleteDocFee(lenderId, row?.id);
      setLoading(false);

      return resp;
    },
  };

  const handleChangeCheckbox = useCallback(async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    await requestUpdateTitleToDeal(lenderId, checked);
  }, [lenderId]);

  return (
    <>
      <Box width="30%">
        <FormControlLabel
          name="AddTitile"
          label={<Typography fontWeight="bold">Add Title Fees to Deal</Typography>}
          control={
            <Checkbox
              checked={includeTitleFeeInDeal}
              onChange={handleChangeCheckbox}
            />
          }
        />
      </Box>

      {!!modalData &&
        <FormulaModal
          apiRef={apiRef}
          modalData={modalData}
          lenderId={lenderId}
          setLoading={setLoading}
          loading={loading}
          handleClose={() => setModalData(null)}
        />
      }

      <PhoenixGrid
        autoHeight
        withHeader
        isFlexible
        autoRowHeight
        apiRef={apiRef}
        name="Doc Fee"
        height="auto"
        gridId={`Doc Fee-${lenderId}`}
        pageSize={10}
        columns={columns}
        loading={!data || loading}
        rows={loading ? [] : data}
        actions={gridActions}
        newRowModel={newRowModel}
        validateSchema={docFeesValidationSchema}
      />
    </>
  );
};
